import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Theme, makeStyles, createStyles, Typography } from '@bb-ui/react-library';
import { DialogProps } from '@bb-ui/react-library/dist/components/Dialog/Dialog.types';
import {
  DefaultButton,
  PrimaryButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  SearchBox,
} from '@bb-ui/react-library/dist/components';
import { useSnackbar } from 'hooks/useSnackbar';
import useRestApi from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { orderBy, chunk } from 'lodash';
import {
  AUTHORIZATION_SERVICES,
  AUTHORIZATION_PERMISSIONS,
  AuthorizationService,
} from 'hooks/useAuthorization.types';

const CATEGORIES = ['Houston', 'Learn Cloud Tooling(LCT)', 'General'] as const;
type Category = (typeof CATEGORIES)[number];

const getCategory = (perm: string): Category => {
  if (AUTHORIZATION_SERVICES.some((s) => perm.startsWith(`${s}.`))) return 'Houston';
  if (perm.startsWith('lct.')) return 'Learn Cloud Tooling(LCT)';
  return 'General';
};

const ROW_HEIGHT = 48;
const SEARCH_HEIGHT = 56;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    addGroupInput: {
      margin: theme.spacing(2, 0, 1),
    },
    modalContent: {
      display: 'flex',
    },
    categoryList: {
      width: '14rem',
      borderRight: `1px solid ${theme.palette.grey[300]}`,
      overflowY: 'auto',
    },
    categoryHeader: {
      padding: theme.spacing(1, 2),
    },
    categoryItem: {
      padding: theme.spacing(1, 2),
      cursor: 'pointer',
    },
    categoryItemSelected: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    permissionsContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: theme.spacing(2),
    },
    uiHeader: {
      display: 'flex',
      marginBottom: theme.spacing(1),
    },
    uiContainer: {
      display: 'flex',
      flex: 1,
      overflow: 'hidden',
    },
    serviceList: {
      width: '12rem',
      borderRight: `1px solid ${theme.palette.grey[300]}`,
      overflowY: 'auto',
    },
    serviceItem: {
      padding: theme.spacing(1, 2),
      cursor: 'pointer',
    },
    serviceItemSelected: {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
    },
    permissionList: {
      flex: 1,
      overflowY: 'auto',
      padding: theme.spacing(1),
    },
    searchWrapper: {
      height: SEARCH_HEIGHT,
      display: 'flex',
      alignItems: 'center',
    },
    tableWrapper: {
      flex: 1,
      overflowY: 'auto',
    },
    row: {
      display: 'flex',
      height: ROW_HEIGHT,
    },
    clickableCell: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      flex: '0 0 25%',
      paddingRight: theme.spacing(2),
      '&:hover': {
        backgroundColor: theme.palette.grey[200],
      },
    },
  }),
);

export interface EditGroupDialogProps extends DialogProps {
  id: string;
  open: boolean;
  group: { id: string; name: string; permissions?: string[]; type?: string; description?: string };
  onClose: () => void;
}

export const EditGroupDialog: React.FC<EditGroupDialogProps> = ({ id, open, group, onClose }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const { data, fetch } = useRestApi(apiUrl('authz', 'permissions'), { manual: true });
  const {
    doPatch,
    loadingRequests,
    succeededRequests,
    failedRequests,
    clearSucceededRequests,
    clearFailedRequests,
  } = useRestApi(apiUrl('sso', 'fndsGroups'), { manual: true });

  const apiPermissions = React.useMemo<string[]>(
    () => (Array.isArray(data) ? data : (data?.permissions ?? [])),
    [data],
  );

  const mergedPermissions = React.useMemo(
    () =>
      Array.from(
        new Set([
          ...AUTHORIZATION_SERVICES.flatMap((s) =>
            AUTHORIZATION_PERMISSIONS.map((p) => `${s}.${p}`),
          ),
          ...apiPermissions,
          ...(group.permissions || []),
        ]),
      ),
    [apiPermissions, group.permissions],
  );

  const [selectedPermissions, setSelectedPermissions] = React.useState<Set<string>>(new Set());
  const [showModal, setShowModal] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [category, setCategory] = React.useState<Category>('Houston');
  const [selectedService, setSelectedService] = React.useState<AuthorizationService>(
    AUTHORIZATION_SERVICES[0],
  );

  React.useEffect(() => {
    if (open) {
      fetch();
      setSelectedPermissions(new Set(group.permissions || []));
      setCategory('Houston');
      setSelectedService(AUTHORIZATION_SERVICES[0]);
    }
  }, [open, group.permissions, fetch]);

  React.useEffect(() => {
    if (succeededRequests.length) {
      enqueueSnackbar(
        t('groupPermissionsDialog.successMessage', {
          name: group.name,
          action: 'updated',
        }),
        { variant: 'success' },
      );
      clearSucceededRequests();
      onClose();
    }
  }, [succeededRequests, clearSucceededRequests, enqueueSnackbar, onClose, group.name, t]);

  React.useEffect(() => {
    if (failedRequests.length) {
      enqueueSnackbar(
        t('groupPermissionsDialog.failureMessage', {
          name: group.name,
          action: 'updated',
          message: failedRequests[0].error.message,
        }),
        { variant: 'error' },
      );
      clearFailedRequests();
    }
  }, [failedRequests, clearFailedRequests, enqueueSnackbar, group.name, t]);

  const selectedCount = (cat: Category) =>
    Array.from(selectedPermissions).filter((p) => getCategory(p) === cat).length;

  const selectedServiceCount = (service: AuthorizationService) =>
    Array.from(selectedPermissions).filter((p) => p.startsWith(`${service}.`)).length;

  const toggle = (perm: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) {
        next.delete(perm);
      } else {
        next.add(perm);
      }
      return next;
    });
  };

  const save = () => {
    doPatch(group.id, { permissions: Array.from(selectedPermissions) });
  };

  const rowsPerCategory = React.useMemo(
    () =>
      CATEGORIES.map(
        (cat) => Math.ceil(mergedPermissions.filter((p) => getCategory(p) === cat).length / 4) || 1,
      ),
    [mergedPermissions],
  );
  const maxRows = Math.max(...rowsPerCategory);
  const tableHeight = maxRows * ROW_HEIGHT;

  const filtered = React.useMemo(() => {
    const perms = mergedPermissions
      .filter((p) => getCategory(p) === category)
      .filter((p) => p.toLowerCase().includes(searchTerm.toLowerCase()));
    return chunk(
      orderBy(perms, (p) => p.toLowerCase(), ['asc']),
      4,
    );
  }, [mergedPermissions, category, searchTerm]);

  return (
    <>
      <Dialog id={id} open={open} onClose={onClose}>
        {/* Dialog title indicating the edit action */}
        <DialogTitle onClose={onClose} closeLabel={t('global.close')}>
          {t('groupPermissionsDialog.title', { dialogType: t('global.edit') })}
        </DialogTitle>
        <DialogContent>
          <TextField
            className={classes.addGroupInput}
            label={t('groupPermissionsDialog.groupName')}
            value={group.name}
            fullWidth
            required
            disabled
          />
          <TextField
            className={classes.addGroupInput}
            label={t('groupPermissionsDialog.groupType')}
            value={group.type || ''}
            fullWidth
            required
            disabled
          />
          <TextField
            className={classes.addGroupInput}
            label={t('groupPermissionsDialog.groupDesc')}
            value={group.description || ''}
            fullWidth
            multiline
            required
            disabled
          />
          {/* Button to open the permissions selection modal */}
          <DefaultButton onClick={() => setShowModal(true)}>
            {t('groupPermissionsDialog.selectPermissions', { count: selectedPermissions.size })}
          </DefaultButton>
          {/* Summary text indicating the number of selected permissions */}
          <Typography variant="body2" color="textSecondary">
            {selectedPermissions.size > 0
              ? t('groupPermissionsDialog.permissionsSelected', { count: selectedPermissions.size })
              : t('groupPermissionsDialog.noPermissionsSelected')}
          </Typography>
        </DialogContent>

        {/* Form action buttons: Cancel and Save */}
        <DialogActions>
          <DefaultButton onClick={onClose}>{t('global.cancel')}</DefaultButton>
          <PrimaryButton onClick={save} disabled={loadingRequests}>
            {t('global.save')}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Modal dialog for selecting group permissions */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth={false} fullWidth>
        {/* Modal title displaying the current count of selected permissions */}
        <DialogTitle onClose={() => setShowModal(false)} closeLabel={t('global.close')}>
          {t('groupPermissionsDialog.selectPermissions', { count: selectedPermissions.size })}
        </DialogTitle>

        <DialogContent>
          <div className={classes.modalContent}>
            {/* Section for displaying the list of categories */}
            <div className={classes.categoryList}>
              <Typography
                variant="subtitle1"
                className={classes.categoryHeader}
                style={{ fontWeight: 'bold' }}
              >
                {t('groupPermissionsDialog.categories')}
              </Typography>
              {CATEGORIES.map((cat) => (
                // Each category item with active highlighting and a count of selected permissions if applicable
                <div
                  key={cat}
                  className={[
                    classes.categoryItem,
                    category === cat && classes.categoryItemSelected,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setCategory(cat)}
                >
                  {cat}
                  {selectedCount(cat) > 0 && ` (${selectedCount(cat)})`}
                </div>
              ))}
            </div>

            {/* Section for displaying permissions based on the selected category */}
            <div className={classes.permissionsContainer}>
              {category === 'Houston' ? (
                <>
                  {/* Header row for services and permissions columns */}
                  <div className={classes.uiHeader}>
                    <Typography variant="subtitle1" style={{ width: '12rem', fontWeight: 'bold' }}>
                      {t('groupPermissionsDialog.serviceHeader')}
                    </Typography>
                    <Typography variant="subtitle1" style={{ flex: 1, fontWeight: 'bold' }}>
                      {t('groupPermissionsDialog.permissionsHeader')}
                    </Typography>
                  </div>

                  {/* Container dividing the services list and the permissions table */}
                  <div className={classes.uiContainer}>
                    {/* Left side: List of available authorization services */}
                    <div className={classes.serviceList}>
                      {AUTHORIZATION_SERVICES.map((service) => (
                        // Each service item with selection highlighting and a count of permissions if any are selected
                        <div
                          key={service}
                          className={[
                            classes.serviceItem,
                            selectedService === service && classes.serviceItemSelected,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() => setSelectedService(service)}
                        >
                          {service}
                          {selectedServiceCount(service) > 0 &&
                            ` (${selectedServiceCount(service)})`}
                        </div>
                      ))}
                    </div>

                    {/* Right side: Table displaying permissions for the selected service */}
                    <div className={classes.permissionList}>
                      <Table>
                        <TableBody>
                          {AUTHORIZATION_PERMISSIONS.map((perm) => {
                            const key = `${selectedService}.${perm}`;
                            return (
                              <TableRow key={key} className={classes.row}>
                                <TableCell
                                  className={classes.clickableCell}
                                  onClick={() => toggle(key)}
                                >
                                  <Checkbox
                                    checked={selectedPermissions.has(key)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggle(key)}
                                  />
                                  {perm}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Search bar for filtering permissions */}
                  <div className={classes.searchWrapper}>
                    <SearchBox
                      inputId="search-permissions"
                      label={t('groupPermissionsDialog.searchPermissions')}
                      value={searchTerm}
                      onChange={setSearchTerm}
                      onSearch={setSearchTerm}
                    />
                  </div>

                  {/* Table displaying filtered permissions based on the search query */}
                  <div className={classes.tableWrapper} style={{ height: tableHeight }}>
                    <Table>
                      <TableBody>
                        {filtered.map((row, idx) => (
                          <TableRow key={idx} className={classes.row}>
                            {row.map((perm) => (
                              <TableCell
                                key={perm}
                                className={classes.clickableCell}
                                onClick={() => toggle(perm)}
                              >
                                <Checkbox
                                  checked={selectedPermissions.has(perm)}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => toggle(perm)}
                                />
                                {perm}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
